import { useSyncExternalStore } from "react";

import type { AdminSessionResponse } from "../contract/types";
import { legacyKeyOf, readWithLegacyFallback } from "../app/legacyStorage";

/**
 * Session d'**administration de plateforme** (CDC §7.1) — régime distinct de la
 * session de poste (`session.ts`). Le compte admin est nominatif (exception
 * assumée CDC §7.3) ; le jeton (opaque, révocable, ~8 h) part en
 * `Authorization: Bearer` sur les seules routes `/admin/**`.
 *
 * Persistée dans `localStorage` pour survivre à un rechargement de l'onglet
 * d'admin ; jamais transmise ailleurs qu'à l'API Sentinelle.
 */
const STORAGE_KEY = "sentinelle.admin.session.v1";
const LEGACY_KEY = legacyKeyOf(STORAGE_KEY);

export interface StoredAdminSession {
  token: string;
  username: string;
  expiresAt: string;
}

type Listener = () => void;

function read(): StoredAdminSession | null {
  try {
    const raw = readWithLegacyFallback(STORAGE_KEY, LEGACY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredAdminSession;
    if (typeof parsed?.token === "string" && typeof parsed?.username === "string") return parsed;
    return null;
  } catch {
    return null;
  }
}

function write(value: StoredAdminSession | null): void {
  try {
    if (value) localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* mode privé / stockage bloqué : la session reste en mémoire seulement */
  }
}

class AdminSessionStore {
  private current: StoredAdminSession | null = read();
  private readonly listeners = new Set<Listener>();

  get(): StoredAdminSession | null {
    return this.current;
  }

  getToken(): string | null {
    return this.current?.token ?? null;
  }

  set(session: AdminSessionResponse): void {
    this.current = { token: session.token, username: session.username, expiresAt: session.expiresAt };
    write(this.current);
    this.emit();
  }

  clear(): void {
    this.current = null;
    write(null);
    this.emit();
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(): void {
    for (const l of [...this.listeners]) l();
  }
}

/** Instance unique pour toute l'application. */
export const adminSessionStore = new AdminSessionStore();

/** Session admin courante (réactif). `null` = non connecté. */
export function useAdminSession(): StoredAdminSession | null {
  return useSyncExternalStore(
    (cb) => adminSessionStore.subscribe(cb),
    () => adminSessionStore.get(),
    () => null,
  );
}
